import { useForm } from '@abyss/web/hooks/useForm';
import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { storage } from '@abyss/web/tools/storage';
import { Accordion } from '@abyss/web/ui/Accordion';
import { Button } from '@abyss/web/ui/Button';
import { Checkbox } from '@abyss/web/ui/Checkbox';
import { CheckboxGroup } from '@abyss/web/ui/CheckboxGroup';
import { Divider } from '@abyss/web/ui/Divider';
import { Flex } from '@abyss/web/ui/Flex';
import { FormProvider } from '@abyss/web/ui/FormProvider';
import { Heading } from '@abyss/web/ui/Heading';
import { RadioGroup } from '@abyss/web/ui/RadioGroup';
import { Text } from '@abyss/web/ui/Text';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { Constants } from '../../../common/Constants';
import { isUmrMember } from '../../../common/Utils/memberUtils/memberUtils';
import { PortalContext } from '../../../context/PortalContext';
import { useConfig } from '../../../frontends/ProviderSearch/context/Analytics';
import { getLanguage } from '../../../frontends/ProviderSearch/context/Internationalization/helpers';
import { useLagoon } from '../../../hooks/useLagoon';
import { useTypeaheadStore } from '../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../store/useTypeaheadStore/typeaheadStore';
import { removeSpecialCharactersUsingMatch } from '../../../utils/smartChoice.utils';
import { adobeLinkTrackEvent } from '../../AdobeTagging/adobeLinkTrackEvent';
import { ConstantsLagoon } from '../../ConstantsLagoon';
import { phoneOnly } from '../../ConstantsStyles';
import { ContentWrapper } from '../../ContentWrapper';
import { getLagoonMessage } from './lagoonUtils';
import { properties } from './OnboardingForm';
import {
  AccordionContainer,
  ClearAllButton,
  HeadingText,
  StyledDrawer,
  StyledMarkdown,
  ToggleSwitchField,
} from './SmartChoice.styled';
import { determineIsSuperUser } from './superUserUtils';
import {
  countDifferences,
  countPreferences,
  formatPreferenceData,
  getLanguagesSpokenValues,
  getSelectedGender,
  getSelectedLanguage,
} from './utils';
import { VirtualizedListCheckbox } from './VirtualizedListCheckbox';

export interface RecommendationProp {
  createdDate?: string;
  distance?: string;
  evngApptAvbl?: string;
  hasDismissedCoachMark?: string;
  hasDismissedCoachMarkWeb?: string;
  hasSetPreferences?: string;
  lastUpdatedDate?: string;
  onlineApptSchdlAvbl?: string;
  providerBoardCertStatus?: string;
  providerGender?: string;
  uuid?: string;
  virtualApptAvbl?: string;
  wkndApptAvbl?: string;
  languagesSpoken?: string[];
  hasDefaultPreferences?: string;
  memberRandomizedKey?: string;
}
export interface Props {
  isOpen: boolean;
  handleCloseDrawer: () => void;
  onClickSaveAndUpdate: ({}) => void;
  preferencesData: RecommendationProp;
  preferencesFormFromProviderDetail: boolean;
  isSuperUser?: boolean;
  submitButtonLabel?: string;
}

export const getUserSelections = async (
  form: any,
  currentSelectedLanguage?: string
): Promise<string> => {
  let userSelections = '';
  if (form?.distance) {
    userSelections += `Distance with in: ${form?.distance} miles | `;
  }
  if (form?.virtualApptAvbl) {
    userSelections += `Virtual appointments | `;
  }
  if (form?.onlineApptSchdlAvbl) {
    userSelections += `Online appointment scheduling | `;
  }
  if (form?.evngApptAvbl) {
    userSelections += `Evening appointments | `;
  }
  if (form?.wkndApptAvbl) {
    userSelections += `Weekend appointments | `;
  }
  if (form?.languagesSpoken?.length) {
    const selectedLanguages = await getSelectedLanguage(
      form?.languagesSpoken,
      currentSelectedLanguage ?? ''
    );
    userSelections += `Languages spoken: ${selectedLanguages
      ?.map((item) => removeSpecialCharactersUsingMatch(item ?? ''))
      ?.join(';')} | `;
  }
  if (form?.providerGender?.length) {
    userSelections += `Gender: ${getSelectedGender(
      form?.providerGender,
      genderList
    )?.join(';')} | `;
  }
  if (form?.providerBoardCertStatus) {
    userSelections += `Board certified: on | `;
  }
  if (userSelections?.endsWith(' | ')) {
    userSelections = userSelections?.slice(0, -3);
  }
  return userSelections
};

const distanceMiles = ['1', '5', '10', '25', '50', '100', '120'];
const genderList = [
  { value: 'M', title: 'Male' },
  { value: 'F', title: 'Female' },
];

export const PreferencesForm = ({
  isOpen,
  handleCloseDrawer,
  preferencesData,
  onClickSaveAndUpdate,
  preferencesFormFromProviderDetail,
  isSuperUser: isSuperUserProp,
  submitButtonLabel,
}: Props) => {
  const { t } = useTranslation();
  const mobileScreen = useMediaQuery(phoneOnly);
  const [preferenceCount, setPreferenceCount] = useState(0);
  const [disabledSubmit, setDisabledSubmit] = useState(0);
  const [languagesSpoken, setLanguagesSpoken] = useState<
    { title: string; value: string }[]
  >([]);

  const lagoonData = useLagoon('ui-messaging')();
  const allConfigs = useLagoon('config')();

  const isSuperUserValue = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.SUPER_USER
  );
  const isSuperUser =
    isSuperUserProp ?? determineIsSuperUser(allConfigs, isSuperUserValue);

  const PREFERENCES_FORM_HEADING_DESCRIPTION = getLagoonMessage(
    lagoonData,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_HEADING_DESCRIPTION
  );
  const PREFERENCES_FORM_PREFERENCES_APPLIED = getLagoonMessage(
    lagoonData,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_PREFERENCES_APPLIED
  );
  const PREFERENCES_FORM_CONVENIENCE_TITLE_DESC = getLagoonMessage(
    lagoonData,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_CONVENIENCE_TITLE_DESC
  );
  const PREFERENCES_FORM_DISTANCE_WITHIN = getLagoonMessage(
    lagoonData,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.PREFERENCES_FORM_DISTANCE_WITHIN
  );
  const PREFERENCES_FORM_VIRTUAL_APPOINTMENTS = getLagoonMessage(
    lagoonData,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_VIRTUAL_APPOINTMENTS
  );
  const PREFERENCES_FORM_ONLINE_APPOINTMENT_SCHEDULING = getLagoonMessage(
    lagoonData,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_ONLINE_APPOINTMENT_SCHEDULING
  );
  const PREFERENCES_FORM_EVENING_APPOINTMENTS = getLagoonMessage(
    lagoonData,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_EVENING_APPOINTMENTS
  );
  const PREFERENCES_FORM_WEEKEND_APPOINTMENTS = getLagoonMessage(
    lagoonData,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_WEEKEND_APPOINTMENTS
  );
  const PREFERENCES_FORM_PERSONAL_FIT_TITLE_DESC = getLagoonMessage(
    lagoonData,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_PERSONAL_FIT_TITLE_DESC
  );
  const PREFERENCES_FORM_LANGUAGES_SPOKEN = getLagoonMessage(
    lagoonData,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.PREFERENCES_FORM_LANGUAGES_SPOKEN
  );
  const PREFERENCES_FORM_GENDER = getLagoonMessage(
    lagoonData,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.PREFERENCES_FORM_GENDER
  );
  const PREFERENCES_FORM_BOARD_CERTIFIED = getLagoonMessage(
    lagoonData,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.PREFERENCES_FORM_BOARD_CERTIFIED
  );
  const PREFERENCES_FORM_SEARCH_PROVIDER = getLagoonMessage(
    lagoonData,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.PREFERENCES_FORM_SEARCH_PROVIDER
  );
  const PREFERENCES_FORM_NO_PREFERENCES_SET = getLagoonMessage(
    lagoonData,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_NO_PREFERENCES_SET
  );
  const PREFERENCES_FORM_CLEAR_ALL = getLagoonMessage(
    lagoonData,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.PREFERENCES_FORM_CLEAR_ALL
  );
  const PREFERENCES_FORM_WITHIN = getLagoonMessage(
    lagoonData,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.PREFERENCES_FORM_WITHIN
  );
  const PREFERENCES_FORM_MILE = getLagoonMessage(
    lagoonData,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.PREFERENCES_FORM_MILE
  );
  const PREFERENCES_FORM_MILES = getLagoonMessage(
    lagoonData,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.PREFERENCES_FORM_MILES
  );
  const PREFERENCES_FORM_UPDATE_PROVIDER_SCORE = getLagoonMessage(
    lagoonData,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_UPDATE_PROVIDER_SCORE
  );

  const UPDATE_SEARCH_RESULTS = getLagoonMessage(
    lagoonData,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.UPDATE_SEARCH_RESULTS
  );

  const SMART_CHOICE_PES_LANG_CODES = useConfig(
    ConstantsLagoon.CONFIG.SMART_CHOICE_PES_LANG_CODES
  );

  const { code: currentSelectedLanguage } = getLanguage();

  const form = useForm({
    defaultValues: {
      distance: null,
      virtualApptAvbl: false,
      onlineApptSchdlAvbl: false,
      evngApptAvbl: false,
      wkndApptAvbl: false,
      // call_support: false,
      languagesSpoken: [],
      providerGender: [],
      providerBoardCertStatus: false,
    },
    values: formatPreferenceData(preferencesData),
  });

  const { search } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      search: state.typeaheadSearchStore.search,
    }))
  );

  // Reset form when preferencesData changes (e.g., after API refetch)
  // This ensures the form always reflects the latest saved preferences
  useEffect(() => {
    if (preferencesData) {
      const formattedData = formatPreferenceData(preferencesData);
      form.reset(formattedData);
    }
    // If languagesSpoken exists in preferencesData, fetch the language names and set the selectedLanguagesText accordingly
    if (preferencesData?.languagesSpoken) {
      let languages = preferencesData?.languagesSpoken;
      if (typeof languages === 'string' && languages !== '') {
        languages = (languages as string).split(',');
      }
      if (Array.isArray(languages) && languages.length > 0) {
        onChangeFetchSelectedLanguages(languages);
      } else {
        setSelectedLanguagesText(PREFERENCES_FORM_NO_PREFERENCES_SET?.title);
      }
    }
  }, [preferencesData]);

  // Use form.watch() to properly subscribe to form value changes
  const formValues = form.watch();

  // Recalculate preference count when form values or preferencesData changes
  useEffect(() => {
    if (formValues) {
      const formFields = { ...formValues };
      const preferencesDataFields = formatPreferenceData(preferencesData);

      const difference = countDifferences(formFields, preferencesDataFields);
      const enabledPreferences = countPreferences(formFields);
      setDisabledSubmit(difference);
      setPreferenceCount(enabledPreferences);
    }
  }, [formValues, preferencesData]);

  useEffect(() => {
    const fetchLanguages = async () => {
      const LANGUAGES_SPOKEN = await getLanguagesSpokenValues(
        currentSelectedLanguage,
        SMART_CHOICE_PES_LANG_CODES
      );
      setLanguagesSpoken(LANGUAGES_SPOKEN);
    };

    fetchLanguages();
  }, [currentSelectedLanguage, SMART_CHOICE_PES_LANG_CODES]);

  const onSubmit = async () => {
    const {
      distance,
      virtualApptAvbl,
      onlineApptSchdlAvbl,
      evngApptAvbl,
      wkndApptAvbl,
      languagesSpoken,
      providerGender,
      providerBoardCertStatus,
    } = formValues;
    const payload = {
      uuid: preferencesData?.uuid,
      hasSetPreferences: 'y',
      hasDismissedCoachMark: preferencesData?.hasDismissedCoachMark,
      hasDismissedCoachMarkWeb: 'y',
      distance: distance || '',
      virtualApptAvbl: virtualApptAvbl ? 'y' : 'n',
      onlineApptSchdlAvbl: onlineApptSchdlAvbl ? 'y' : 'n',
      evngApptAvbl: evngApptAvbl ? 'y' : 'n',
      wkndApptAvbl: wkndApptAvbl ? 'y' : 'n',
      providerBoardCertStatus: providerBoardCertStatus ? 'y' : 'n',
      providerGender: providerGender?.length ? providerGender?.join(',') : '',
      languagesSpoken: languagesSpoken?.length
        ? languagesSpoken?.join(',')
        : '',
      isSuperUser,
    };

    onClickSaveAndUpdate(payload);

    const userSelections = await getUserSelections(
      formValues,
      currentSelectedLanguage
    );

    adobeLinkTrackEvent({
      userSelections,
      name: buttonTitle,
      location: 'modal:Set preferences',
    });
  };

  const onClearHandler = () => {
    const formFields = JSON.parse(JSON.stringify(form.getValues()));

    const enabledPreferences = countPreferences(formFields);
    setPreferenceCount(enabledPreferences);

    form.reset({
      distance: '',
      virtualApptAvbl: false,
      onlineApptSchdlAvbl: false,
      evngApptAvbl: false,
      wkndApptAvbl: false,
      languagesSpoken: [],
      providerGender: [],
      providerBoardCertStatus: false,
    });
    adobeLinkTrackEvent({
      name: 'clear all',
      location: 'modal:Set preferences',
    });
  };

  // Submit button - priority order:
  // 1) submitButtonLabel (literal string provided by parent - highest priority)
  // 2) contextual values depending on location (provider detail or search)
  // 3) final fallback: PREFERENCES_FORM_SEARCH_PROVIDER
  const buttonTitle = React.useMemo((): string => {
    const rawLabel = submitButtonLabel?.trim();
    if (rawLabel && rawLabel.length > 0) return rawLabel;

    // Contextual behavior
    if (preferencesFormFromProviderDetail) {
      return (
        PREFERENCES_FORM_UPDATE_PROVIDER_SCORE?.title || t('Save preferences')
      );
    }

    if (search) {
      return UPDATE_SEARCH_RESULTS?.title || t('Save preferences');
    }

    // Final fallback
    return PREFERENCES_FORM_SEARCH_PROVIDER?.title || t('Save preferences');
  }, [
    submitButtonLabel,
    preferencesFormFromProviderDetail,
    search,
    PREFERENCES_FORM_UPDATE_PROVIDER_SCORE,
    UPDATE_SEARCH_RESULTS,
    t,
  ]);
  const [selectedLanguagesText, setSelectedLanguagesText] =
    useState<string>('');

  const onChangeFetchSelectedLanguages = async (languages: string[]) => {
    if (languages?.length) {
      const selectedLanguages = await getSelectedLanguage(
        languages,
        currentSelectedLanguage
      );

      const formattedLanguages = selectedLanguages
        ?.map((item) => removeSpecialCharactersUsingMatch(item ?? ''))
        ?.join(', ');

      setSelectedLanguagesText(formattedLanguages);
    } else {
      setSelectedLanguagesText(PREFERENCES_FORM_NO_PREFERENCES_SET?.title);
    }
  };
  const { portalData } = useContext(PortalContext);
  const isUmr = isUmrMember(portalData?.portalName);
  return (
    <React.Fragment>
      <StyledDrawer
        data-auto-testid="smart-choice-preferences-drawer"
        data-testid="smart-choice-preferences-drawer"
        isOpen={isOpen}
        onClose={() => {
          handleCloseDrawer();
          adobeLinkTrackEvent({
            name: 'Close',
            location: 'modal:Set preferences',
          });
        }}
        position={properties.position}
        size={properties.drawerSize}
        title={
          <Text
            color="$primary"
            css={{ fontWeight: 400, fontSize: '32.45px !important' }}
            data-testid="smart-choice-preferences-heading"
          >
            {PREFERENCES_FORM_HEADING_DESCRIPTION?.title}
          </Text>
        }
      >
        <ContentWrapper
          style={{
            padding: mobileScreen ? '0px' : '16px',
            '& form': { marginLeft: '-16px', marginRight: '-16px' },
          }}
        >
          <Flex justify="space-between">
            <Text
              css={{ display: 'block', fontSize: '16px', fontWeight: 700 }}
              data-testid="set-preferences-sub-heading"
            >
              {`${preferenceCount} ${PREFERENCES_FORM_PREFERENCES_APPLIED?.title}`}
            </Text>
            <ClearAllButton
              data-testid="clear-all-preferences"
              onClick={onClearHandler}
              tabIndex={0}
            >
              <Text
                css={{
                  color: '$interactive1',
                  cursor: 'pointer',
                  display: 'block',
                  fontSize: '16px',
                  fontWeight: 700,
                }}
              >
                {PREFERENCES_FORM_CLEAR_ALL?.title}
              </Text>
            </ClearAllButton>
          </Flex>
          <StyledMarkdown
            css={{
              color: '$black !important',
              fontSize: '14px',
              fontWeight: 400,
              marginBottom: '16px',
            }}
            data-testid="set-preferences-sub-heading-desc"
          >
            {PREFERENCES_FORM_HEADING_DESCRIPTION?.message}
          </StyledMarkdown>

          <Flex>
            <HeadingText
              data-auto-testid="preferences-convenience-heading"
              data-testid="preferences-convenience-heading"
              offset={2}
            >
              {PREFERENCES_FORM_CONVENIENCE_TITLE_DESC?.title}
            </HeadingText>
            <Text
              css={{
                fontSize: '16px',
                marginBottom: '16px',
              }}
            >
              {PREFERENCES_FORM_CONVENIENCE_TITLE_DESC?.message}
            </Text>
          </Flex>

          <FormProvider
            data-auto-testid="set-preferences-form"
            data-testid="set-preferences-form"
            onSubmit={onSubmit}
            state={form}
          >
            <AccordionContainer isCollapsible>
              <Accordion.Item value="distance">
                <Accordion.Trigger>
                  <Flex>
                    <Accordion.Header
                      data-auto-testid="preferences-distance"
                      data-testid="preferences-distance"
                      subText={
                        formValues?.distance
                          ? `${PREFERENCES_FORM_WITHIN?.title} ${
                              formValues?.distance
                            } ${
                              formValues?.distance && formValues?.distance > 1
                                ? PREFERENCES_FORM_MILES?.title
                                : PREFERENCES_FORM_MILE?.title
                            }`
                          : PREFERENCES_FORM_NO_PREFERENCES_SET?.title
                      }
                    >
                      <Heading offset={3}>
                        {PREFERENCES_FORM_DISTANCE_WITHIN?.title}
                      </Heading>
                    </Accordion.Header>
                  </Flex>
                </Accordion.Trigger>
                <Accordion.Content>
                  <RadioGroup label="" model="distance">
                    {distanceMiles?.map((item) => (
                      <RadioGroup.Radio
                        data-auto-testid={`preferences-distance-miles`}
                        data-testid={`preferences-distance-miles`}
                        key={item}
                        label={`${item} ${
                          parseInt(item) > 1
                            ? PREFERENCES_FORM_MILES?.title
                            : PREFERENCES_FORM_MILE?.title
                        }`}
                        value={item}
                      />
                    ))}
                  </RadioGroup>
                </Accordion.Content>
              </Accordion.Item>
            </AccordionContainer>
            <Divider />
            <Flex>
              <ToggleSwitchField
                data-auto-testid="preferences"
                data-testid="preferences"
                label={PREFERENCES_FORM_VIRTUAL_APPOINTMENTS?.title}
                model="virtualApptAvbl"
              />
              <Divider />
              {!isUmr && (
                <>
                  <ToggleSwitchField
                    data-auto-testid="preferences-online"
                    data-testid="preferences"
                    label={
                      PREFERENCES_FORM_ONLINE_APPOINTMENT_SCHEDULING?.title
                    }
                    model="onlineApptSchdlAvbl"
                  />
                  <Divider />
                </>
              )}
              <ToggleSwitchField
                data-auto-testid="preferences-evening"
                data-testid="preferences"
                label={PREFERENCES_FORM_EVENING_APPOINTMENTS?.title}
                model="evngApptAvbl"
              />
              <Divider />
              <ToggleSwitchField
                data-auto-testid="preferences-weekend"
                data-testid="preferences"
                label={PREFERENCES_FORM_WEEKEND_APPOINTMENTS?.title}
                model="wkndApptAvbl"
              />
              <Divider />
            </Flex>
            <Flex css={{ padding: '0px 16px' }}>
              <HeadingText
                data-auto-testid="preferences-personal-fit-heading"
                data-testid="preferences-personal-fit-heading"
                offset={2}
              >
                {PREFERENCES_FORM_PERSONAL_FIT_TITLE_DESC?.title}
              </HeadingText>
              <Text
                css={{
                  fontSize: '16px',
                  marginBottom: '16px',
                }}
              >
                {PREFERENCES_FORM_PERSONAL_FIT_TITLE_DESC?.message}
              </Text>
            </Flex>
            <AccordionContainer isCollapsible>
              <Accordion.Item value="language">
                <Accordion.Trigger>
                  <Accordion.Header
                    data-auto-testid="preferences-languages-spoken"
                    data-testid="preferences-languages-spoken"
                    subText={
                      formValues?.languagesSpoken === ''
                        ? PREFERENCES_FORM_NO_PREFERENCES_SET?.title
                        : formValues?.languagesSpoken?.length
                        ? selectedLanguagesText
                        : PREFERENCES_FORM_NO_PREFERENCES_SET?.title
                    }
                  >
                    <Heading offset={3}>
                      {PREFERENCES_FORM_LANGUAGES_SPOKEN?.title}
                    </Heading>
                  </Accordion.Header>
                </Accordion.Trigger>
                <Accordion.Content>
                  <CheckboxGroup
                    data-auto-testid="language-values-box"
                    data-testid="language-values-box"
                    label=""
                    model="languagesSpoken"
                    onChange={(languages) => {
                      onChangeFetchSelectedLanguages(languages);
                    }}
                  >
                    <VirtualizedListCheckbox
                      data={languagesSpoken}
                      form={form}
                    />
                  </CheckboxGroup>
                </Accordion.Content>
              </Accordion.Item>
            </AccordionContainer>
            <Divider />
            <AccordionContainer isCollapsible>
              <Accordion.Item value="gender">
                <Accordion.Trigger>
                  <Accordion.Header
                    data-auto-testid="preferences-gender"
                    data-testid="preferences-gender"
                    subText={
                      formValues?.providerGender === ''
                        ? PREFERENCES_FORM_NO_PREFERENCES_SET?.title
                        : formValues?.providerGender?.length
                        ? formValues.providerGender
                            .map((value) => {
                              const genderItem = genderList.find(
                                (item) => item.value === value
                              );
                              return genderItem ? t(genderItem.title) : '';
                            })
                            .filter((item) => item !== '')
                            .join(', ')
                        : PREFERENCES_FORM_NO_PREFERENCES_SET?.title
                    }
                  >
                    <Heading offset={3}>
                      {PREFERENCES_FORM_GENDER?.title}
                    </Heading>
                  </Accordion.Header>
                </Accordion.Trigger>
                <Accordion.Content>
                  <CheckboxGroup label="" model="providerGender">
                    {genderList.map((item) => (
                      <Checkbox
                        data-auto-testid="preferences-gender-checkbox"
                        data-testid="preferences-gender-checkbox"
                        key={item?.value}
                        label={t(item?.title)}
                        value={item?.value}
                      />
                    ))}
                  </CheckboxGroup>
                </Accordion.Content>
              </Accordion.Item>
            </AccordionContainer>
            <Divider />
            <ToggleSwitchField
              data-auto-testid="preferences-board-certified"
              data-testid="preferences"
              label={PREFERENCES_FORM_BOARD_CERTIFIED?.title}
              model="providerBoardCertStatus"
            />
            <Divider />
            <Flex
              css={{
                backgroundColor: '$white',
                bottom: '0',
                padding: '16px',
                position: 'sticky',
                zIndex: 1,
              }}
            >
              <Button
                css={{ width: '100%' }}
                data-testid="search-for-provider-button"
                isDisabled={disabledSubmit === 0}
                type="submit"
              >
                {buttonTitle}
              </Button>
            </Flex>
          </FormProvider>
        </ContentWrapper>
      </StyledDrawer>
    </React.Fragment>
  );
};
